import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CardGiftcard as GiftIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { MagicBackground } from '../components/MagicBackground';
import { Reward } from '../types';

export default function Rewards() {
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingReward, setEditingReward] = useState<Reward | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'percentage_discount' as Reward['type'],
    pointsRequired: '',
    discountPercentage: '',
    discountAmount: '',
    freeItemId: '',
    freeServiceId: '',
    customReward: '',
    status: 'active' as 'active' | 'inactive' | 'expired',
    minPurchaseAmount: '',
    maxUses: '',
    validUntil: '',
  });

  useEffect(() => {
    loadRewards();
  }, []);

  const loadRewards = async () => {
    try {
      setLoading(true);
      const data = await apiService.getRewards();
      setRewards(data);
    } catch (error: any) {
      console.error('Error loading rewards:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to load rewards', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (reward?: Reward) => {
    if (reward) {
      setEditingReward(reward);
      const conditions = reward.conditions || {};
      setFormData({
        name: reward.name,
        description: reward.description || '',
        type: reward.type,
        pointsRequired: reward.pointsRequired.toString(),
        discountPercentage: reward.discountPercentage?.toString() || '',
        discountAmount: reward.discountAmount ? (reward.discountAmount / 100).toFixed(2) : '',
        freeItemId: reward.freeItemId || '',
        freeServiceId: reward.freeServiceId || '',
        customReward: reward.customReward || '',
        status: reward.status === 'expired' ? 'inactive' : reward.status,
        minPurchaseAmount: conditions.minPurchaseAmount ? (conditions.minPurchaseAmount / 100).toFixed(2) : '',
        maxUses: conditions.maxUses ? conditions.maxUses.toString() : '',
        validUntil: conditions.validUntil || '',
      });
    } else {
      setEditingReward(null);
      setFormData({
        name: '',
        description: '',
        type: 'percentage_discount',
        pointsRequired: '',
        discountPercentage: '',
        discountAmount: '',
        freeItemId: '',
        freeServiceId: '',
        customReward: '',
        status: 'active',
        minPurchaseAmount: '',
        maxUses: '',
        validUntil: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingReward(null);
  };

  const handleSave = async () => {
    try {
      const rewardData: Partial<Reward> = {
        name: formData.name,
        description: formData.description || undefined,
        type: formData.type,
        pointsRequired: parseInt(formData.pointsRequired),
        status: formData.status,
        conditions: {},
      };

      // Type-specific fields
      if (formData.type === 'percentage_discount') {
        rewardData.discountPercentage = parseFloat(formData.discountPercentage);
      } else if (formData.type === 'fixed_discount') {
        rewardData.discountAmount = Math.round(parseFloat(formData.discountAmount) * 100); // Convert to cents
      } else if (formData.type === 'free_item') {
        rewardData.freeItemId = formData.freeItemId;
      } else if (formData.type === 'free_service') {
        rewardData.freeServiceId = formData.freeServiceId;
      } else if (formData.type === 'custom') {
        rewardData.customReward = formData.customReward;
      }

      // Conditions
      if (formData.minPurchaseAmount) {
        rewardData.conditions!.minPurchaseAmount = Math.round(parseFloat(formData.minPurchaseAmount) * 100);
      }
      if (formData.maxUses) {
        rewardData.conditions!.maxUses = parseInt(formData.maxUses);
      }
      if (formData.validUntil) {
        rewardData.conditions!.validUntil = formData.validUntil;
      }

      if (editingReward) {
        await apiService.updateReward(editingReward.rewardId, rewardData);
        setSnackbar({ open: true, message: 'Reward updated successfully!', severity: 'success' });
      } else {
        await apiService.createReward(rewardData);
        setSnackbar({ open: true, message: 'Reward created successfully!', severity: 'success' });
      }

      handleCloseDialog();
      loadRewards();
    } catch (error: any) {
      console.error('Error saving reward:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to save reward', severity: 'error' });
    }
  };

  const handleDelete = async (rewardId: string) => {
    if (!window.confirm('Are you sure you want to delete this reward?')) {
      return;
    }

    try {
      await apiService.deleteReward(rewardId);
      setSnackbar({ open: true, message: 'Reward deleted successfully!', severity: 'success' });
      loadRewards();
    } catch (error: any) {
      console.error('Error deleting reward:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to delete reward', severity: 'error' });
    }
  };

  const getRewardDisplay = (reward: Reward) => {
    switch (reward.type) {
      case 'percentage_discount':
        return `${reward.discountPercentage}% off`;
      case 'fixed_discount':
        return `$${(reward.discountAmount! / 100).toFixed(2)} off`;
      case 'free_item':
        return `Free item (ID: ${reward.freeItemId})`;
      case 'free_service':
        return `Free service (ID: ${reward.freeServiceId})`;
      case 'custom':
        return reward.customReward || 'Custom reward';
      default:
        return 'Unknown';
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <MagicBackground />
        <CircularProgress sx={{ color: '#667eea', position: 'relative', zIndex: 1 }} size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
      minHeight: '100vh', 
      position: 'relative',
      py: 4 
    }}>
      <MagicBackground />
      
      <Box maxWidth="lg" sx={{ mx: 'auto', px: 3, position: 'relative', zIndex: 1 }}>
        <Box mb={4} display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Box display="flex" alignItems="center" gap={2} mb={1}>
              <GiftIcon sx={{ fontSize: 40, color: '#667eea' }} />
              <Typography 
                variant="h3" 
                fontWeight="bold" 
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Rewards Program
              </Typography>
            </Box>
            <Typography variant="h6" color="rgba(255,255,255,0.7)">
              Manage rewards that customers can claim with their points
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              bgcolor: '#667eea',
              '&:hover': { bgcolor: '#5568d3' },
            }}
          >
            Create Reward
          </Button>
        </Box>

        {rewards.length === 0 ? (
          <Card sx={{ background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <GiftIcon sx={{ fontSize: 64, color: 'rgba(255,255,255,0.3)', mb: 2 }} />
              <Typography variant="h6" color="white" mb={1}>
                No rewards yet
              </Typography>
              <Typography variant="body2" color="rgba(255,255,255,0.7)" mb={3}>
                Create your first reward to start your loyalty program
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{
                  bgcolor: '#667eea',
                  '&:hover': { bgcolor: '#5568d3' },
                }}
              >
                Create Reward
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {rewards.map((reward) => (
              <Grid item xs={12} md={6} key={reward.rewardId}>
                <Card sx={{ 
                  background: 'rgba(255, 255, 255, 0.05)', 
                  backdropFilter: 'blur(20px)', 
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'rgba(102, 126, 234, 0.3)',
                    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.1)',
                  },
                }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                      <Box>
                        <Typography variant="h6" color="white" fontWeight="bold">
                          {reward.name}
                        </Typography>
                        {reward.description && (
                          <Typography variant="body2" color="rgba(255,255,255,0.7)" mt={0.5}>
                            {reward.description}
                          </Typography>
                        )}
                      </Box>
                      <Box display="flex" gap={1}>
                        <IconButton size="small" onClick={() => handleOpenDialog(reward)} sx={{ color: '#667eea' }}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(reward.rewardId)} sx={{ color: '#ff6b6b' }}>
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                      <StarIcon sx={{ color: '#FFD700', fontSize: 20 }} />
                      <Typography variant="body1" color="white" fontWeight="medium">
                        {reward.pointsRequired} points
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="rgba(255,255,255,0.7)" mb={1}>
                      Reward: {getRewardDisplay(reward)}
                    </Typography>
                    <Box display="flex" gap={1} mt={2}>
                      <Chip 
                        label={reward.status} 
                        size="small" 
                        color={reward.status === 'active' ? 'success' : 'default'}
                        sx={{ 
                          bgcolor: reward.status === 'active' ? 'rgba(67, 233, 123, 0.2)' : 'rgba(255,255,255,0.1)',
                          color: 'white',
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Create/Edit Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              background: 'rgba(26, 31, 58, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <DialogTitle sx={{ color: 'white', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
            {editingReward ? 'Edit Reward' : 'Create Reward'}
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Reward Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Reward Type</InputLabel>
                  <Select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as Reward['type'] })}
                    label="Reward Type"
                  >
                    <MenuItem value="percentage_discount">Percentage Discount</MenuItem>
                    <MenuItem value="fixed_discount">Fixed Discount</MenuItem>
                    <MenuItem value="free_item">Free Item</MenuItem>
                    <MenuItem value="free_service">Free Service</MenuItem>
                    <MenuItem value="custom">Custom Reward</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Points Required"
                  value={formData.pointsRequired}
                  onChange={(e) => setFormData({ ...formData, pointsRequired: e.target.value })}
                  required
                  sx={{ mb: 2 }}
                />
              </Grid>
              {formData.type === 'percentage_discount' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Discount Percentage"
                    value={formData.discountPercentage}
                    onChange={(e) => setFormData({ ...formData, discountPercentage: e.target.value })}
                    required
                    inputProps={{ min: 1, max: 100 }}
                    sx={{ mb: 2 }}
                  />
                </Grid>
              )}
              {formData.type === 'fixed_discount' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Discount Amount ($)"
                    value={formData.discountAmount}
                    onChange={(e) => setFormData({ ...formData, discountAmount: e.target.value })}
                    required
                    inputProps={{ min: 0, step: 0.01 }}
                    sx={{ mb: 2 }}
                  />
                </Grid>
              )}
              {formData.type === 'free_item' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Menu Item ID"
                    value={formData.freeItemId}
                    onChange={(e) => setFormData({ ...formData, freeItemId: e.target.value })}
                    required
                    helperText="Enter the ID of the menu item to give for free"
                    sx={{ mb: 2 }}
                  />
                </Grid>
              )}
              {formData.type === 'free_service' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Service ID"
                    value={formData.freeServiceId}
                    onChange={(e) => setFormData({ ...formData, freeServiceId: e.target.value })}
                    required
                    helperText="Enter the ID of the service to give for free"
                    sx={{ mb: 2 }}
                  />
                </Grid>
              )}
              {formData.type === 'custom' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Custom Reward Description"
                    value={formData.customReward}
                    onChange={(e) => setFormData({ ...formData, customReward: e.target.value })}
                    required
                    sx={{ mb: 2 }}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                <Typography variant="subtitle2" color="white" mb={2}>
                  Conditions (Optional)
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Minimum Purchase Amount ($)"
                  value={formData.minPurchaseAmount}
                  onChange={(e) => setFormData({ ...formData, minPurchaseAmount: e.target.value })}
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max Uses Per Customer"
                  value={formData.maxUses}
                  onChange={(e) => setFormData({ ...formData, maxUses: e.target.value })}
                  inputProps={{ min: 1 }}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="date"
                  label="Valid Until"
                  value={formData.validUntil}
                  onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.status === 'active'}
                      onChange={(e) => setFormData({ ...formData, status: e.target.checked ? 'active' : 'inactive' })}
                    />
                  }
                  label="Active"
                  sx={{ color: 'white' }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', p: 2 }}>
            <Button onClick={handleCloseDialog} sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              sx={{
                bgcolor: '#667eea',
                '&:hover': { bgcolor: '#5568d3' },
              }}
            >
              {editingReward ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
}

